import type { Window } from '@risksmart-app/scheduler-engine/src/index';
import { RecurrenceConfigError } from '@risksmart-app/scheduler-engine/src/index';

import { previewOccurrences, resolveScheduleWindows } from '@/utils/scheduler';

import type { RecurrenceValues } from './useRecurrenceValues';

/**
 * `incomplete` and `unavailable` are separated on purpose: an empty preview that
 * cannot say which of the two it is leaves a genuinely impossible configuration
 * looking identical to one the planner has not finished typing.
 */
export type OccurrencePreview =
  | { status: 'incomplete' }
  | { status: 'ready'; windows: Window[] }
  | { status: 'unavailable' };

/** Exported so the preview heading names the same number it renders. */
export const PREVIEW_OCCURRENCE_COUNT = 3;

export const buildOccurrencePreview = (
  values: RecurrenceValues
): OccurrencePreview => {
  const schedule = resolveScheduleWindows(values);
  if (schedule === null) {
    return { status: 'incomplete' };
  }

  try {
    const windows = previewOccurrences(schedule, PREVIEW_OCCURRENCE_COUNT);

    return windows.length === 0
      ? { status: 'unavailable' }
      : { status: 'ready', windows };
  } catch (error) {
    // Only a config the engine refuses to expand — a seed far enough ahead that
    // a later period leaves the `Date` range. Anything else is a bug here, not
    // something the planner can fix, so it must not be swallowed.
    if (error instanceof RecurrenceConfigError) {
      return { status: 'unavailable' };
    }

    throw error;
  }
};
